"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollToTop } from "@/components/ui/scroll-to-top";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Gavel,
  Shield,
  Users,
} from "lucide-react";

const sections = [
  { id: "acceptance", titleKey: "sections.acceptance", icon: CheckCircle2 },
  { id: "description", titleKey: "sections.description", icon: FileText },
  { id: "accounts", titleKey: "sections.accounts", icon: Users },
  { id: "usage", titleKey: "sections.usage", icon: Shield },
  { id: "payments", titleKey: "sections.payments", icon: Gavel },
  { id: "ip", titleKey: "sections.ip", icon: FileText },
  { id: "limitation", titleKey: "sections.limitation", icon: AlertTriangle },
  { id: "termination", titleKey: "sections.termination", icon: Clock },
];

export default function TermsOfServicePage() {
  const { t } = useJsonTranslation("TermsOfService");

  return (
    <div className="container mx-auto px-4 py-8 pt-24 sm:py-16">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="bg-leaf-500/20 mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl">
            <FileText className="text-leaf-600 dark:text-leaf-400 h-8 w-8" />
          </div>
          <h1 className="text-foreground mb-4 text-4xl font-bold">
            {t("title")}
          </h1>
          <div className="text-muted-foreground flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{t("lastUpdated")}</span>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <Card variant="glass" className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
              {t("tableOfContents")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2">
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="group hover:bg-foreground/5 flex items-center gap-2 rounded-lg p-2 transition-colors"
                  >
                    <span className="text-leaf-600 dark:text-leaf-400 w-6 text-sm font-semibold">
                      {index + 1}.
                    </span>
                    <Icon className="text-muted-foreground group-hover:text-leaf-600 dark:group-hover:text-leaf-400 h-4 w-4 transition-colors" />
                    <span className="text-muted-foreground group-hover:text-foreground text-sm">
                      {t(section.titleKey)}
                    </span>
                  </a>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        <div className="space-y-8">
          {/* Acceptance */}
          <Card id="acceptance" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                1. {t("sections.acceptance")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.acceptance.p1")}</p>
              <p>{t("content.acceptance.p2")}</p>
            </CardContent>
          </Card>

          {/* Description */}
          <Card id="description" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                2. {t("sections.description")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.description.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                    <span>{t(`content.description.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Accounts */}
          <Card id="accounts" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                3. {t("sections.accounts")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.accounts.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                    <span>{t(`content.accounts.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Acceptable Use */}
          <Card id="usage" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                4. {t("sections.usage")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.usage.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <AlertTriangle className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                    <span>{t(`content.usage.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Payments */}
          <Card id="payments" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gavel className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                5. {t("sections.payments")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.payments.p")}</p>
              <Separator />
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="bg-foreground/5 rounded-lg p-4">
                  <h5 className="text-foreground font-medium">
                    {t("content.payments.freeTrial")}
                  </h5>
                  <p className="mt-1 text-sm">
                    {t("content.payments.freeTrialDesc")}
                  </p>
                </div>
                <div className="bg-foreground/5 rounded-lg p-4">
                  <h5 className="text-foreground font-medium">
                    {t("content.payments.cancellation")}
                  </h5>
                  <p className="mt-1 text-sm">
                    {t("content.payments.cancellationDesc")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* IP */}
          <Card id="ip" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                6. {t("sections.ip")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.ip.p")}</p>
              <div className="bg-leaf-500/10 border-leaf-500/30 rounded-lg border p-4">
                <p className="text-leaf-600 dark:text-leaf-400">
                  {t("content.ip.patent")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Limitation */}
          <Card id="limitation" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="text-leaf-400 h-5 w-5" />
                7. {t("sections.limitation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.limitation.p1")}</p>
              <p>{t("content.limitation.p2")}</p>
            </CardContent>
          </Card>

          {/* Termination */}
          <Card id="termination" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                8. {t("sections.termination")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.termination.p1")}</p>
              <p>{t("content.termination.p2")}</p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card
            variant="glass"
            className="from-leaf-500/10 to-leaf-500/10 bg-gradient-to-r"
          >
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground mb-4">
                {t("content.contact.p")}
              </p>
              <a
                href="mailto:legal@vidseeds.ai"
                className="text-leaf-600 dark:text-leaf-400 font-medium hover:underline"
              >
                legal@vidseeds.ai
              </a>
            </CardContent>
          </Card>
        </div>
      </div>

      <ScrollToTop />
    </div>
  );
}
